import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Paperclip, Send, X, Loader2, Image as ImageIcon, FileText, Mic, Trash2, Check, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { app as appApi } from "../lib/api";
import { HoverBorderGradient } from "./ui/hover-border-gradient";
import MultiSendDialog from "./MultiSendDialog";

/**
 * ChatInput — totalmente local (uncontrolled) para que
 * a digitação nunca cause re-render no ChatPage / lista de mensagens.
 *
 * Expõe uma API imperativa via ref:
 *   ref.current.setText(value)  -> preenche o textarea
 *   ref.current.focus()         -> foca o textarea
 *   ref.current.clear()         -> limpa tudo
 */
const ChatInput = forwardRef(function ChatInput(
    { onSend, disabled, placeholder = "Digite sua mensagem..." },
    ref
) {
    const [files, setFiles] = useState([]); // { filename, url, content_type }
    const [uploading, setUploading] = useState(false);
    const [, forceBtnUpdate] = useState(0); // usado pra reavaliar o estado do botão enviar
    const [multi, setMulti] = useState(1);       // disparo múltiplo (1 = normal)
    const [multiOpen, setMultiOpen] = useState(false);

    // Recording state
    const [recording, setRecording] = useState(false);
    const [recordSec, setRecordSec] = useState(0);
    const mediaRecorderRef = useRef(null);
    const recordChunksRef = useRef([]);
    const recordTimerRef = useRef(null);
    const recordCancelledRef = useRef(false);

    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);

    const autoResize = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 200) + "px";
    };

    useImperativeHandle(ref, () => ({
        setText(value) {
            if (textareaRef.current) {
                textareaRef.current.value = value;
                autoResize();
                forceBtnUpdate((n) => n + 1);
            }
        },
        focus() {
            textareaRef.current?.focus();
        },
        clear() {
            if (textareaRef.current) {
                textareaRef.current.value = "";
                textareaRef.current.style.height = "auto";
            }
            setFiles([]);
            forceBtnUpdate((n) => n + 1);
        },
    }));

    useEffect(() => {
        autoResize();
        return () => {
            // cleanup de gravação ao desmontar
            if (recordTimerRef.current) clearInterval(recordTimerRef.current);
            const mr = mediaRecorderRef.current;
            if (mr && mr.state !== "inactive") {
                try { mr.stop(); } catch { /* ignore */ }
                mr.stream?.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    const handleFiles = async (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length === 0) return;
        setUploading(true);
        try {
            const res = await appApi.upload(selected, "chat");
            const uploaded = (res.uploaded || []).map((u) => ({
                filename: u.filename,
                url: u.url,
                content_type: u.content_type,
            }));
            setFiles((prev) => [...prev, ...uploaded]);
            if (res.failures?.length) {
                toast.error(`Falha ao enviar ${res.failures.length} arquivo(s).`);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || "Erro ao enviar arquivos.";
            toast.error(detail);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeFile = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    // ---------------- RECORDING ----------------

    const startRecording = async () => {
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            toast.error("Gravação de áudio não suportada neste navegador.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : MediaRecorder.isTypeSupported("audio/mp4")
                ? "audio/mp4"
                : "audio/webm";
            const mr = new MediaRecorder(stream, { mimeType });
            recordChunksRef.current = [];
            recordCancelledRef.current = false;
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                if (recordCancelledRef.current) {
                    recordChunksRef.current = [];
                    return;
                }
                const useType = mimeType.split(";")[0];
                const ext = useType.includes("mp4") ? "m4a" : "webm";
                const blob = new Blob(recordChunksRef.current, { type: useType });
                recordChunksRef.current = [];
                if (blob.size < 500) {
                    toast.error("Áudio muito curto.");
                    return;
                }
                const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: useType });
                setUploading(true);
                try {
                    const res = await appApi.upload([file], "chat");
                    const uploaded = (res.uploaded || []).map((u) => ({
                        filename: u.filename,
                        url: u.url,
                        content_type: u.content_type,
                    }));
                    setFiles((prev) => [...prev, ...uploaded]);
                } catch (err) {
                    const detail = err?.response?.data?.detail || "Falha ao enviar áudio.";
                    toast.error(detail);
                } finally {
                    setUploading(false);
                }
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
            setRecordSec(0);
            recordTimerRef.current = setInterval(() => {
                setRecordSec((s) => {
                    const next = s + 1;
                    // trava em 5 minutos
                    if (next >= 300) {
                        stopRecording(false);
                    }
                    return next;
                });
            }, 1000);
        } catch (err) {
            if (err && err.name === "NotAllowedError") {
                toast.error("Permita o acesso ao microfone para gravar áudio.");
            } else {
                toast.error("Não foi possível iniciar a gravação.");
            }
        }
    };

    const stopRecording = (cancel) => {
        const mr = mediaRecorderRef.current;
        if (recordTimerRef.current) {
            clearInterval(recordTimerRef.current);
            recordTimerRef.current = null;
        }
        recordCancelledRef.current = !!cancel;
        if (mr && mr.state !== "inactive") {
            try { mr.stop(); } catch { /* ignore */ }
        }
        setRecording(false);
        setRecordSec(0);
    };

    // ---------------- SUBMIT ----------------

    const submit = (e) => {
        e?.preventDefault?.();
        if (disabled || uploading || recording) return;
        const text = (textareaRef.current?.value || "").trim();
        if (!text && files.length === 0) return;

        onSend({ text, files, multi });
        setMulti(1);

        if (textareaRef.current) {
            textareaRef.current.value = "";
            textareaRef.current.style.height = "auto";
        }
        setFiles([]);
        forceBtnUpdate((n) => n + 1);
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    // Estado do botão enviar derivado do valor atual do DOM + files + disabled
    const currentText = textareaRef.current?.value || "";
    const canSend = !disabled && !uploading && !recording && ((currentText.trim() !== "") || files.length > 0);
    const hasText = currentText.trim() !== "";
    const showMicButton = !hasText && files.length === 0 && !disabled && !uploading;

    return (
        <div className="w-full" data-testid="chat-input-container">
            {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 px-1" data-testid="chat-input-attachments">
                    {files.map((f, i) => {
                        const isImg = (f.content_type || "").startsWith("image/");
                        const isAudio = (f.content_type || "").startsWith("audio/");
                        const Icon = isImg ? ImageIcon : isAudio ? Volume2 : FileText;
                        return (
                            <div key={i} className="flex items-center gap-2 pl-2 pr-1 py-1 border border-border rounded-sm bg-card text-xs">
                                <Icon className="w-3.5 h-3.5 text-primary" />
                                <span className="truncate max-w-[160px]">{isAudio ? "Áudio" : f.filename}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(i)}
                                    className="p-1 hover:bg-muted rounded-sm"
                                    data-testid={`remove-attachment-${i}`}
                                    aria-label="Remover anexo"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {recording ? (
                <HoverBorderGradient
                    as="div"
                    containerClassName="rounded-full w-full block"
                    className="w-full !p-2 flex items-center gap-2"
                    highlight="#ef4444"
                    data-testid="chat-recording-bar"
                >
                    <button
                        type="button"
                        onClick={() => stopRecording(true)}
                        data-testid="chat-record-cancel"
                        className="p-2.5 rounded-full text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                        aria-label="Cancelar gravação"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                    <div className="flex-1 flex items-center gap-2 px-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse shrink-0" />
                        <span className="text-xs font-mono text-muted-foreground">Gravando</span>
                        <span className="flex-1" />
                        <span className="font-mono text-sm text-foreground tabular-nums" data-testid="chat-record-time">
                            {formatTime(recordSec)}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => stopRecording(false)}
                        data-testid="chat-record-stop"
                        className="btn-primary p-2.5 rounded-full shrink-0"
                        aria-label="Finalizar gravação"
                    >
                        <Check className="w-5 h-5" strokeWidth={2.5} />
                    </button>
                </HoverBorderGradient>
            ) : (
                <HoverBorderGradient
                    as="form"
                    containerClassName="rounded-[28px] w-full block"
                    className="w-full !p-2 flex items-end gap-1"
                    onSubmit={submit}
                >
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || disabled}
                        data-testid="chat-attach-btn"
                        className="p-2.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0 disabled:opacity-50"
                        aria-label="Anexar arquivo"
                    >
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => setMultiOpen(true)}
                        disabled={disabled}
                        data-testid="chat-multi-btn"
                        title="Disparo múltiplo — mesmo pedido em várias conversas"
                        aria-label="Disparo múltiplo"
                        className={`shrink-0 px-2.5 py-1.5 my-1 rounded-full font-mono text-xs font-bold transition-all border disabled:opacity-50 ${
                            multi > 1
                                ? "bg-primary text-white border-primary shadow-[0_0_12px_rgba(34,197,94,0.45)]"
                                : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                        }`}
                    >
                        {multi}x
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFiles}
                        accept="*/*"
                        data-testid="chat-file-input"
                    />
                    <textarea
                        ref={textareaRef}
                        defaultValue=""
                        onInput={() => { autoResize(); forceBtnUpdate((n) => n + 1); }}
                        onKeyDown={onKey}
                        rows={1}
                        placeholder={placeholder}
                        disabled={disabled}
                        data-testid="chat-input-textarea"
                        className="bg-transparent border-none outline-none resize-none flex-1 text-sm py-2.5 px-2 placeholder:text-muted-foreground/70 max-h-[200px] disabled:opacity-50"
                    />
                    {showMicButton ? (
                        <button
                            type="button"
                            onClick={startRecording}
                            data-testid="chat-mic-btn"
                            className="p-2.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                            aria-label="Gravar áudio"
                        >
                            <Mic className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={!canSend}
                            data-testid="chat-send-btn"
                            className="btn-primary p-2.5 rounded-full shrink-0"
                            aria-label="Enviar"
                        >
                            <Send className="w-5 h-5" strokeWidth={2.2} />
                        </button>
                    )}
                </HoverBorderGradient>
            )}

            <MultiSendDialog
                open={multiOpen}
                onClose={() => setMultiOpen(false)}
                value={multi}
                onApply={(n) => setMulti(n)}
            />
        </div>
    );
});

function formatTime(sec) {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
}

export default ChatInput;
